import React, { useMemo, useContext, useEffect, useState, useRef } from 'react';
import type { Text } from 'src/components/translate';
import { AppContext } from 'src/components/app-context';
import * as S from './styles';
import type { Column } from './table-header';
import { TableHeader } from './table-header';
import type {
  Product,
  ProductCollectionSortDirection,
  ProductCollectionPropertyColumn,
} from '../../../../../types';
import { getSortKeyForProperty } from '../../../utils/properties';
import { getPropertyColumnName } from '../../../utils/property-columns';
import { DEFAULT_PROPERTY_COLUMN_COUNT } from '../constants';
import HighlightedSearchResult from '../highlighted-search-result';
import { ContainerAdsTable } from './ads-container';
import { ProductRow } from './product-row';
import type { ProductComparisonProps } from '../types';
import { useNewColumnIndication } from './new-column-indication';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { WideContext } from 'src/contexts/wide-context';
import { useSelector } from 'react-redux';
import type { AppState } from 'src/reducers';

type DefaultColumn = {
  translation: Text<'category'> | Text<'common'>;
  sortKey?: string;
  colSpan?: number;
};

const defaultColumns: DefaultColumn[] = [
  {
    translation: 'product-list.name-column-heading',
    sortKey: 'name',
  },
  { translation: 'product-list.price-column-heading', sortKey: 'price' },
  {
    translation: 'product-list.stock-column-heading',
    sortKey: 'property.in_stock',
  },
  {
    translation: 'product-list.rating-column-heading',
    sortKey: 'user_reviews',
  },
  {
    translation: 'sort.popularity',
    sortKey: 'popularity',
  },
];

type Props = {
  products: Product[];
  currentSort: string;
  sortDirection: ProductCollectionSortDirection;
  propertyColumns: ProductCollectionPropertyColumn[];
} & ProductComparisonProps;

export const TableLayout = ({
  products,
  currentSort,
  sortDirection,
  propertyColumns,
  ...productComparisonProps
}: Props) => {
  const { t } = useContext(AppContext);

  const columns = useMemo<Column[]>(() => {
    const getTranslation = (
      translation: Text<'category'> | Text<'common'>
    ): string => {
      const translationForCommon = t('common', translation as Text<'common'>);
      if (translationForCommon !== translation) {
        return translationForCommon as string;
      }
      return t('category', translation as Text<'category'>) as string;
    };

    return [
      ...defaultColumns.map(column => ({
        ...column,
        name: getTranslation(column.translation),
      })),
      ...propertyColumns.map(column => ({
        name: getPropertyColumnName(column, t),
        sortKey: getSortKeyForProperty(column.id),
      })),
    ];
  }, [propertyColumns, t]);

  const scrollableRef = useRef<HTMLDivElement | null>(null);

  const { indicateNewColumn } = useNewColumnIndication(
    columns.length,
    scrollableRef.current
  );

  const { isWide } = useContext(WideContext);

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const checkScrolled = () => {
      setScrolled(window.scrollX > 0);
    };

    window.addEventListener('scroll', checkScrolled, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScrolled);
    };
  }, []);

  const tab = useSelector<AppState>(state => state.areas.sidebar.tab);
  const isSidebarOpen = tab != null && tab !== 'none';

  const table = (
    <S.Table
      scrollable={propertyColumns.length > DEFAULT_PROPERTY_COLUMN_COUNT}
      cellSpacing="0"
      data-test="ProductLayoutTable"
    >
      <TableHeader
        currentSort={currentSort}
        sortDirection={sortDirection}
        columns={columns}
        indicateNewColumn={indicateNewColumn}
        sticky={isWide}
        scrolled={scrolled}
        sidebarOpen={isSidebarOpen}
      />
      <tbody>
        <ContainerAdsTable numOfColumns={4 + propertyColumns.length}>
          {products.map((product, index) => {
            return (
              <ProductRow
                key={product.id}
                product={product}
                propertyColumns={propertyColumns}
                indicateNewColumn={indicateNewColumn}
                highlightedName={
                  product.highlight ? (
                    <HighlightedSearchResult>
                      {product.highlight}
                    </HighlightedSearchResult>
                  ) : undefined
                }
                onClick={(link: string) => {
                  trackInternalNavigationClick({
                    scope: InternalNavigationScope.TABLE_LAYOUT_PRODUCT_CARD,
                    nextPage: link,
                    index,
                  });
                }}
                stickyFirstColumn={isWide}
                hasScrolled={scrolled}
                isSidebarOpen={isSidebarOpen}
                {...productComparisonProps}
              />
            );
          })}
        </ContainerAdsTable>
      </tbody>
    </S.Table>
  );

  return (
    <S.AdPositioningContext>
      {isWide ? (
        table
      ) : (
        <S.HorizontalScrollPane ref={scrollableRef}>
          {table}
        </S.HorizontalScrollPane>
      )}
    </S.AdPositioningContext>
  );
};
